/**
 * v1.6 / D-KI (D-67) AP3 — the "Lenses" system list (Ref KI_systemliste_lenses).
 *
 * The first system list: system prompts ("Lenses") for the KI chat. System
 * columns (Name, Systemprompt) are locked — value editable, column not
 * deletable (blue header + 🔒). User columns are freely addable/deletable
 * („＋ Spalte"). All workspace members see the lenses, except a lens marked
 * privat, which only its creator sees. Built on the existing list engine via
 * the /system-lists/lenses endpoints.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { ListColumn, ListRow } from "../api";

const cellText = (v: unknown): string =>
  v === null || v === undefined ? "" : String(v);

export const RyfeLenses = () => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const [columns, setColumns] = useState<ListColumn[]>([]);
  const [rows, setRows] = useState<ListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [colMenu, setColMenu] = useState<string | null>(null);
  const [addingCol, setAddingCol] = useState(false);
  const [newLens, setNewLens] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await ryfeApi.getLenses(wsId);
      setColumns(r.columns);
      setRows(r.rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveCell = async (row: ListRow, col: ListColumn, value: string) => {
    if (!wsId || cellText(row.data[col.name]) === value) {
      return;
    }
    setError(null);
    try {
      await ryfeApi.updateLens(wsId, row.id, {
        data: { ...row.data, [col.name]: value },
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen");
    }
  };

  const togglePrivate = async (row: ListRow) => {
    if (!wsId) {
      return;
    }
    try {
      await ryfeApi.updateLens(wsId, row.id, { private: !row.private });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Ändern fehlgeschlagen");
    }
  };

  const deleteLens = async (row: ListRow) => {
    if (!wsId || !window.confirm("Diese Lens löschen?")) {
      return;
    }
    try {
      await ryfeApi.deleteLens(wsId, row.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Löschen fehlgeschlagen");
    }
  };

  const deleteColumn = async (col: ListColumn) => {
    if (!wsId) {
      return;
    }
    setColMenu(null);
    try {
      await ryfeApi.deleteLensColumn(wsId, col.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Spalte nicht löschbar");
    }
  };

  return (
    <div className="ryfe-page">
      <div className="ryfe-breadcrumb">
        <span className="ryfe-muted">🔒 Systemlisten</span>
        <span className="ryfe-muted"> / </span>
        <strong>Lenses</strong>
      </div>
      <h1 className="ryfe-page__title">Systemliste „Lenses"</h1>
      <p className="ryfe-muted" style={{ paddingTop: 0 }}>
        Systemprompts für den KI-Chat. Systemspalten (Name, Systemprompt) sind
        geschützt; eigene Spalten kannst du ergänzen. Alle Mitglieder sehen die
        Lenses — außer eine ist auf „privat" gestellt (dann nur du). Das
        KI-Modell ist in Dify fix, nicht pro Lens.
      </p>

      {error && <div className="ryfe-auth__error">{error}</div>}

      {loading ? (
        <div className="ryfe-muted">Laden…</div>
      ) : (
        <>
          <div className="ryfe-toolbar" style={{ margin: "0.6rem 0" }}>
            <button
              className="ryfe-btn ryfe-btn--primary"
              onClick={() => setNewLens(true)}
            >
              ＋ Neue Lens
            </button>
            <button
              className="ryfe-btn ryfe-btn--ghost"
              onClick={() => setAddingCol(true)}
            >
              ＋ Spalte
            </button>
          </div>

          <div className="ryfe-table-wrap">
            <table className="ryfe-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.id}
                      className={c.locked ? "ryfe-syscol" : undefined}
                    >
                      <span className="ryfe-elmenu__wrap">
                        {c.name} {c.locked ? "🔒" : ""}
                        {!c.locked && (
                          <>
                            {" "}
                            <button
                              className="ryfe-chip"
                              aria-label={`Spalte ${c.name}`}
                              onClick={() =>
                                setColMenu((m) => (m === c.id ? null : c.id))
                              }
                            >
                              ▾
                            </button>
                            {colMenu === c.id && (
                              <div className="ryfe-elmenu__sub" role="menu">
                                <button
                                  className="ryfe-elmenu__item"
                                  onClick={() => deleteColumn(c)}
                                >
                                  🗑 Spalte löschen
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </span>
                    </th>
                  ))}
                  <th aria-label="Privat" />
                  <th aria-label="Aktionen" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 2} className="ryfe-muted">
                      Noch keine Lens — lege mit „＋ Neue Lens" die erste an.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="ryfe-row">
                      {columns.map((c) => (
                        <td key={c.id}>
                          <input
                            className="ryfe-cell-input"
                            defaultValue={cellText(row.data[c.name])}
                            onBlur={(e) => saveCell(row, c, e.target.value)}
                            aria-label={`${c.name} von ${cellText(
                              row.data.Name,
                            )}`}
                          />
                        </td>
                      ))}
                      <td>
                        <label
                          className="ryfe-check"
                          title="Nur für mich sichtbar"
                        >
                          <input
                            type="checkbox"
                            checked={!!row.private}
                            onChange={() => togglePrivate(row)}
                          />
                          <span>privat</span>
                        </label>
                      </td>
                      <td>
                        <button
                          className="ryfe-chip"
                          onClick={() => deleteLens(row)}
                          title="Lens löschen"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {newLens && wsId && (
        <NewLensDialog
          wsId={wsId}
          onClose={() => setNewLens(false)}
          onCreated={() => {
            setNewLens(false);
            void load();
          }}
        />
      )}
      {addingCol && wsId && (
        <AddColumnDialog
          wsId={wsId}
          onClose={() => setAddingCol(false)}
          onCreated={() => {
            setAddingCol(false);
            void load();
          }}
        />
      )}
    </div>
  );
};

const NewLensDialog = ({
  wsId,
  onClose,
  onCreated,
}: {
  wsId: string;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [priv, setPriv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await ryfeApi.createLens(wsId, {
        data: { Name: name, Systemprompt: prompt },
        private: priv,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Neue Lens</h2>
        <label className="ryfe-field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="ryfe-field">
          <span>Systemprompt</span>
          <textarea
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </label>
        <label className="ryfe-check">
          <input
            type="checkbox"
            checked={priv}
            onChange={(e) => setPriv(e.target.checked)}
          />
          <span>Privat (nur für mich sichtbar)</span>
        </label>
        {err && <div className="ryfe-auth__error">{err}</div>}
        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={save}
            disabled={busy || !name.trim()}
          >
            Anlegen
          </button>
        </div>
      </div>
    </div>
  );
};

const AddColumnDialog = ({
  wsId,
  onClose,
  onCreated,
}: {
  wsId: string;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("text");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await ryfeApi.addLensColumn(wsId, { name, type });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Spalte hinzufügen</h2>
        <label className="ryfe-field">
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Sprache"
          />
        </label>
        <label className="ryfe-field">
          <span>Typ</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="text">Text</option>
            <option value="number">Zahl</option>
            <option value="checkbox">Checkbox</option>
            <option value="date">Datum</option>
            <option value="select">Auswahl</option>
          </select>
        </label>
        {err && <div className="ryfe-auth__error">{err}</div>}
        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={save}
            disabled={busy || !name.trim()}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};
